import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'oidc-react'
import { Drawer, List } from '@fullscreendigital/carrefour-dex-fe'
import { toSelectedMenuItems, toMenuItems } from './serializers'
import { getUserStore, getUserRegion } from 'utils/authService'
import { SidebarHeader } from './SidebarHeader'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import cn from 'classnames'
import s from './Sidebar.scss'
import { Box, Typography } from '@mui/material'
import { Icon_v2 } from 'components/core-ui/Icon_v2'

const staticItems = [
  {
    disablePadding: true,
    icon: <Icon_v2 iconName="Home" iconType="Menu" />,
    index: 1,
    primaryText: 'Prima pagina',
    type: 'icon',
    href: '/'
  }

  // {
  //   disablePadding: true,
  //   icon: <Icon_v2 iconName="Notifications" iconType="Menu" />,
  //   index: 2,
  //   primaryText: 'Notificari',
  //   type: 'icon',
  //   href: '/'
  // },
  // {
  //   disablePadding: true,
  //   icon: <Icon_v2 iconName="User" iconType="Menu" />,
  //   index: 3,
  //   primaryText: 'Contul meu',
  //   type: 'icon',
  //   href: '/'
  // }
]

const disabledSx = {
  opacity: 0.5,
  cursor: 'not-allowed',
  pointerEvents: 'auto',
  '&:hover': { backgroundColor: 'transparent !important' },
  '& *': { color: 'rgba(0,0,0,0.38) !important' },

  '& + .MuiCollapse-root .MuiListItem-root': {
    opacity: 0.5,
    cursor: 'not-allowed',
    '& *': { color: 'rgba(0,0,0,0.38) !important' }
  }
}

const Sidebar = ({ isMenuOpen, handleCloseMenu, modules, setSubdomainId, handleLogout }) => {
  const storeName = getUserStore()
  const regionName = getUserRegion()

  const [items, setItems] = useState([])
  const [selectedItem, setSelectedItem] = useState(0)
  const sidebarRef = useRef(null)

  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const disabledModuleIds = useMemo(
    () => new Set((modules || []).filter(m => m.disabled).map(m => m.id)),
    [modules]
  )

  const disabledSubmoduleIds = useMemo(() =>
    (modules ?? []).reduce((ids, m) => {
      if (m?.disabled && Array.isArray(m.submodules)) {
        m.submodules.forEach(sm => ids.add(sm.id))
      }
      return ids
    }, new Set()), [modules])

  useEffect(() => {
    const handleOutsideClick = event => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        handleCloseMenu()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [handleCloseMenu])

  useEffect(() => {
    ;(async () => {
      try {
        const base = toMenuItems(modules)

        const withDisabled = base.map(it => {
          const moduleDisabled = disabledModuleIds.has(it.id)
          const nested = Array.isArray(it.nestedItems)
            ? it.nestedItems.map(ni => {
              const subDisabled = moduleDisabled || disabledSubmoduleIds.has(ni.id)
              return {
                ...ni,
                sx: subDisabled ? disabledSx : undefined,
                ariaDisabled: subDisabled,
                title: subDisabled ? 'Nu aveți niciun magazin asociat acestui cont.' : undefined
              }
            })
            : it.nestedItems

          return {
            ...it,
            sx: moduleDisabled ? disabledSx : undefined,
            ariaDisabled: moduleDisabled,
            title: moduleDisabled ? 'Nu aveți niciun magazin asociat acestui cont.' : undefined,
            nestedItems: nested
          }
        })

        setItems(withDisabled)
      } catch (error) {
        // https://giphy.com/embed/oe33xf3B50fsc :))))
        showNotification({
          message: `Error while setting application module menu items. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    })()
  }, [modules, disabledModuleIds, disabledSubmoduleIds])

  const handleItemClick = (event, item) => {
    event.preventDefault()

    const isDisabled =
      disabledModuleIds.has(item?.id) || disabledSubmoduleIds.has(item?.id)

    if (isDisabled) {
      showNotification({
        message: 'Nu aveți niciun magazin asociat acestui cont.',
        type: 'warning'
      })
      return
    }

    handleCloseMenu()

    const { href, index, id } = item

    navigate(href)
    setSelectedItem(index)
    localStorage.setItem('selectedSubdomainId', id)
    setSubdomainId(id)
  }

  const selectedItems = useMemo(() => {
    const base = toSelectedMenuItems(items, selectedItem)

    return base.map(it => {
      const moduleDisabled = disabledModuleIds.has(it.id)
      const nested = Array.isArray(it.nestedItems)
        ? it.nestedItems.map(ni => {
          const subDisabled = moduleDisabled || disabledSubmoduleIds.has(ni.id)
          return {
            ...ni,
            sx: subDisabled ? disabledSx : undefined,
            ariaDisabled: subDisabled,
            title: subDisabled ? 'Nu aveți niciun magazin asociat acestui cont.' : undefined
          }
        })
        : it.nestedItems

      return {
        ...it,
        sx: moduleDisabled ? disabledSx : undefined,
        ariaDisabled: moduleDisabled,
        title: moduleDisabled ? 'Nu aveți niciun magazin asociat acestui cont.' : undefined,
        nestedItems: nested
      }
    })
  }, [items, selectedItem, disabledModuleIds, disabledSubmoduleIds])

  return (
    <Drawer
      isLeftPersistent
      open={isMenuOpen}
      onClose={handleCloseMenu}
      displayEmptyHeader
      disablePadding
      headerContent={
        <SidebarHeader
          handleCloseMenu={handleCloseMenu}
          storeName={storeName}
          regionName={regionName}
        />
      }
      headerClass={s.drawerHeader}
      paperClassName={s.itemsListContainer}
    >
      <div
        ref={sidebarRef}
        className={cn(
          s.itemsList,
          { [s.padding90]: storeName || regionName },
          { [s.padding130]: storeName && regionName }
        )}
      >
        <nav className={s.section}>
          <List
            subheader={{
              component: 'div',
              content: '',
              style: { fontSize: '70%' }
            }}
            items={staticItems.map(item => ({
              ...item,
              selected: selectedItem === item.index
            }))}
            onItemClick={handleItemClick}
          />
        </nav>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography style={{ margin: '24px 8px 24px 24px', fontSize: '20px', fontWeight: 700 }}>
            My apps
          </Typography>
          <Icon_v2 iconName="Folder" iconType="Icon" />
        </Box>

        <nav className={s.section}>
          <List
            subheader={{
              component: 'div',
              content: '',
              style: { fontSize: '70%' }
            }}
            items={selectedItems}
            onItemClick={handleItemClick}
          />
        </nav>

        <br />
        <nav className={s.section}>
          <List
            subheader={{
              component: 'div',
              content: '',
              style: { fontSize: '70%' }
            }}
            items={[
              {
                disablePadding: true,
                icon: <Icon_v2 iconName="Logout" iconType="Menu" />,
                index: 2,
                primaryText: 'Sign Out',
                type: 'icon'
              }
            ]}
            onItemClick={handleLogout}
          />
        </nav>
      </div>
    </Drawer>
  )
}

export default Sidebar
